/**
 * PricingTable - All tiers comparison
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/utils/cn';
import { useSubscriptionStore } from '@/stores';
import { SUBSCRIPTION_TIERS, SUPPORTED_CURRENCIES } from '../config';
import { PricingCard } from './PricingCard';
import type { SubscriptionTier } from '@/types/subscription';

interface PricingTableProps {
  onSelectTier: (tier: SubscriptionTier, billingPeriod: 'monthly' | 'annual') => void;
}

export function PricingTable({ onSelectTier }: PricingTableProps) {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const currentTier = useSubscriptionStore((state) => state.tier);
  const currency = useSubscriptionStore((state) => state.currency);
  const setCurrency = useSubscriptionStore((state) => state.setCurrency);

  const tiers = Object.values(SUBSCRIPTION_TIERS);

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Header Controls */}
      <div className="flex flex-col items-center gap-3 mb-6 sm:mb-8">
        {/* Billing Period Toggle */}
        <div className="flex items-center gap-1 p-1 bg-white/[0.04] rounded-xl border border-white/[0.08]">
          <button
            onClick={() => setBillingPeriod('monthly')}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
              billingPeriod === 'monthly'
                ? 'bg-hud-primary text-white shadow-sm'
                : 'text-text-secondary hover:text-white'
            )}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingPeriod('annual')}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2',
              billingPeriod === 'annual'
                ? 'bg-hud-primary text-white shadow-sm'
                : 'text-text-secondary hover:text-white'
            )}
          >
            Annual
            <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs font-semibold">
              -33%
            </span>
          </button>
        </div>

        {/* Currency Selector */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted">Currency:</span>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className={cn(
              'px-2 py-1.5 rounded-lg text-xs',
              'bg-white/[0.04] border border-white/[0.08]',
              'text-white',
              'focus:outline-none focus:border-hud-primary'
            )}
          >
            {Object.entries(SUPPORTED_CURRENCIES).map(([code, config]) => (
              <option key={code} value={code}>
                {config.symbol} {code}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        {tiers.map((tier, index) => (
          <motion.div
            key={tier.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <PricingCard
              tier={tier}
              isCurrentTier={currentTier === tier.id}
              billingPeriod={billingPeriod}
              onSelect={() => onSelectTier(tier.id, billingPeriod)}
            />
          </motion.div>
        ))}
      </div>

      {/* Footer info */}
      <div className="mt-8 sm:mt-12 text-center">
        <p className="text-text-secondary text-xs sm:text-sm">
          All plans include a 7-day free trial. Cancel anytime.
        </p>
        <p className="text-text-muted text-[11px] sm:text-xs mt-1.5">
          Prices shown in {SUPPORTED_CURRENCIES[currency]?.name || 'US Dollars'}.
        </p>
      </div>
    </div>
  );
}
